import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Shield, Smartphone, Monitor, Loader2, CheckCircle, XCircle,
  Key, Lock, Trash2, Globe, Clock, ChevronLeft, AlertTriangle,
  QrCode
} from 'lucide-react';
import { toast } from 'sonner';

const SecuritySettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [sessions, setSessions] = useState([]);
  
  // 2FA Setup State
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [setupPassword, setSetupPassword] = useState('');
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [settingUp2FA, setSettingUp2FA] = useState(false);
  const [verifying2FA, setVerifying2FA] = useState(false);
  
  // 2FA Disable State
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disabling2FA, setDisabling2FA] = useState(false);
  
  // Session Management
  const [revokingSession, setRevokingSession] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);

  useEffect(() => {
    loadSecurityInfo();
  }, []);

  const loadSecurityInfo = async () => {
    try {
      const [authRes, sessionsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/auth/sessions')
      ]);
      
      setTwoFactorEnabled(authRes.data.two_factor_enabled || false);
      setSessions(sessionsRes.data || []);
    } catch (error) {
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    // First show password dialog
    setPasswordDialogOpen(true);
  };

  const handleSetup2FAWithPassword = async () => {
    if (!setupPassword) {
      toast.error('Please enter your password');
      return;
    }
    
    setSettingUp2FA(true);
    try {
      const response = await api.post('/auth/2fa/setup', { password: setupPassword });
      setQrCodeUrl(response.data.qr_code_url);
      setSecret(response.data.secret);
      setPasswordDialogOpen(false);
      setSetupDialogOpen(true);
      setSetupPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setSettingUp2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setVerifying2FA(true);
    try {
      await api.post('/auth/2fa/verify', { code: verificationCode });
      toast.success('Two-Factor Authentication enabled!');
      setTwoFactorEnabled(true);
      setSetupDialogOpen(false);
      setVerificationCode('');
      setQrCodeUrl('');
      setSecret('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setDisabling2FA(true);
    try {
      await api.post('/auth/2fa/disable', { code: disableCode });
      toast.success('Two-Factor Authentication disabled');
      setTwoFactorEnabled(false);
      setDisableDialogOpen(false);
      setDisableCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setDisabling2FA(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    setRevokingSession(sessionId);
    try {
      await api.delete(`/auth/sessions/${sessionId}`);
      setSessions(sessions.filter(s => s.id !== sessionId));
      toast.success('Session revoked');
    } catch (error) {
      toast.error('Failed to revoke session');
    } finally {
      setRevokingSession(null);
    }
  };

  const handleRevokeAllSessions = async () => {
    setRevokingAll(true);
    try {
      await api.delete('/auth/sessions');
      setSessions([]);
      toast.success('All sessions revoked. You will be logged out.');
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    } catch (error) {
      toast.error('Failed to revoke sessions');
    } finally {
      setRevokingAll(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getDeviceIcon = (userAgent) => {
    if (!userAgent) return <Globe className="w-5 h-5" />;
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return <Smartphone className="w-5 h-5" />;
    }
    return <Monitor className="w-5 h-5" />;
  };

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return 'Unknown device';
    
    // Extract browser
    let browser = 'Unknown browser';
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';
    
    // Extract OS
    let os = 'Unknown OS';
    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS';
    
    return `${browser} on ${os}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#4ADE80]" />
              Security Settings
            </h1>
            <p className="text-sm text-gray-400">Manage your account security</p>
          </div>
        </div>

        {/* Two-Factor Authentication */}
        <Card className="bg-[#12151a] border-[#1a1d24] mb-6" data-testid="2fa-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Key className="w-5 h-5 text-[#4ADE80]" />
              Two-Factor Authentication
            </CardTitle>
            <CardDescription>
              Add an extra layer of security to your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  twoFactorEnabled ? 'bg-green-500/20' : 'bg-yellow-500/20'
                }`}>
                  {twoFactorEnabled ? (
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-yellow-400" />
                  )}
                </div>
                <div>
                  <p className="text-white font-medium">
                    {twoFactorEnabled ? '2FA Enabled' : '2FA Not Enabled'}
                  </p>
                  <p className="text-sm text-gray-400">
                    {twoFactorEnabled 
                      ? 'Your account is protected with 2FA'
                      : 'Enable 2FA for enhanced security'
                    }
                  </p>
                </div>
              </div>
              
              {twoFactorEnabled ? (
                <Button
                  variant="outline"
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                  onClick={() => setDisableDialogOpen(true)}
                  data-testid="disable-2fa-btn"
                >
                  Disable 2FA
                </Button>
              ) : (
                <Button
                  className="bg-[#4ADE80] text-black hover:bg-[#22C55E]"
                  onClick={handleSetup2FA}
                  disabled={settingUp2FA}
                  data-testid="setup-2fa-btn"
                >
                  {settingUp2FA ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <QrCode className="w-4 h-4 mr-2" />
                      Setup 2FA
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Active Sessions */}
        <Card className="bg-[#12151a] border-[#1a1d24]" data-testid="sessions-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Monitor className="w-5 h-5 text-[#4ADE80]" />
                  Active Sessions
                </CardTitle>
                <CardDescription>
                  Devices where you're currently logged in
                </CardDescription>
              </div>
              {sessions.length > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                  onClick={handleRevokeAllSessions}
                  disabled={revokingAll}
                  data-testid="revoke-all-btn"
                >
                  {revokingAll ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Logout All Devices'
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <div className="text-center py-8">
                <Lock className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No active sessions found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex items-center justify-between p-4 bg-[#0a0c10] rounded-lg border border-[#1a1d24]"
                    data-testid={`session-${session.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#1a1d24] flex items-center justify-center text-gray-400">
                        {getDeviceIcon(session.user_agent)}
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {parseUserAgent(session.user_agent)}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            {session.ip_address || 'Unknown IP'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(session.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {session.is_current ? (
                      <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">
                        Current
                      </span>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleRevokeSession(session.id)}
                        disabled={revokingSession === session.id}
                        data-testid={`revoke-session-${session.id}`}
                      >
                        {revokingSession === session.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Password Dialog for 2FA Setup */}
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent className="bg-[#12151a] border-[#1a1d24] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#4ADE80]" />
                Confirm Your Password
              </DialogTitle>
              <DialogDescription>
                Enter your password to enable two-factor authentication
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Password</Label>
                <Input
                  type="password"
                  placeholder="Enter your password"
                  value={setupPassword}
                  onChange={(e) => setSetupPassword(e.target.value)}
                  className="bg-[#0a0c10] border-[#1a1d24]"
                  data-testid="2fa-password-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setPasswordDialogOpen(false);
                setSetupPassword('');
              }}>
                Cancel
              </Button>
              <Button
                className="bg-[#4ADE80] text-black hover:bg-[#22C55E]"
                onClick={handleSetup2FAWithPassword}
                disabled={settingUp2FA || !setupPassword}
                data-testid="confirm-password-btn"
              >
                {settingUp2FA ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Continue'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 2FA Setup Dialog */}
        <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
          <DialogContent className="bg-[#12151a] border-[#1a1d24] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <QrCode className="w-5 h-5 text-[#4ADE80]" />
                Setup Two-Factor Authentication
              </DialogTitle>
              <DialogDescription>
                Scan the QR code with your authenticator app
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* QR Code */}
              <div className="flex justify-center">
                {qrCodeUrl && (
                  <img 
                    src={qrCodeUrl} 
                    alt="2FA QR Code"
                    className="w-48 h-48 bg-white p-2 rounded-lg"
                    data-testid="2fa-qr-code"
                  />
                )}
              </div>
              
              {/* Manual Entry */}
              <div className="bg-[#0a0c10] p-3 rounded-lg">
                <Label className="text-xs text-gray-400">Manual Entry Key</Label>
                <code className="block text-sm text-[#4ADE80] mt-1 break-all font-mono">
                  {secret}
                </code>
              </div>
              
              {/* Verification Code Input */}
              <div className="space-y-2">
                <Label className="text-white">Enter verification code</Label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="bg-[#0a0c10] border-[#1a1d24] text-center text-2xl tracking-widest"
                  data-testid="2fa-verify-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setSetupDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                className="bg-[#4ADE80] text-black hover:bg-[#22C55E]"
                onClick={handleVerify2FA}
                disabled={verifying2FA || verificationCode.length !== 6}
                data-testid="verify-2fa-btn"
              >
                {verifying2FA ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Enable 2FA'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Disable 2FA Dialog */}
        <Dialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
          <DialogContent className="bg-[#12151a] border-[#1a1d24] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                Disable Two-Factor Authentication
              </DialogTitle>
              <DialogDescription>
                Enter your authenticator code to disable 2FA
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <p className="text-sm text-yellow-400">
                  Warning: Disabling 2FA will make your account less secure. Make sure you have other security measures in place.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Enter verification code</Label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                  className="bg-[#0a0c10] border-[#1a1d24] text-center text-2xl tracking-widest"
                  data-testid="2fa-disable-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDisableDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDisable2FA}
                disabled={disabling2FA || disableCode.length !== 6}
                data-testid="confirm-disable-2fa-btn"
              >
                {disabling2FA ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Disable 2FA'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SecuritySettingsPage;
